import React, {FunctionComponent, useEffect, useMemo, useState} from 'react';

import {Box, TextField} from "@mui/material";
import {useAppSelector} from '../../../../Config/Hooks';
import {RootState} from '../../../../Config/Store';
import {SkidType} from './types/Skid';
import ChartHeatmap from './ChartHeatmap';
import ActivePowerInvertersChart from '../../ActivePower/ActivePowerInvertersChart';

import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import {startOfDay, endOfDay, isWithinInterval} from 'date-fns';
import {LocalDateTimeFormatISO} from "../../../../Utils/DateFormatPatternUtils";
import {ACard, AToggleButtonGroup, Period, Skeleton} from "@atiautomacao/ati-ui-library";
import DataNotFound from "../../../../Shared/Components/DataNotFoundMessage";
import useInterval from "../../../../Shared/Hooks/useInterval";
import {QueryClient, QueryClientProvider} from "react-query";

const PLANT_CARD_HEIGHT = 463;
const CARD_TITLE = "Potência por inversor (PU)";
const CARD_ARIA_LABEL = "dashboard-station-power-pu-chart";

type ViewMode = "chart" | "heatmap";

const buildDayPeriod = (day: Date): Period => ({
    groupBy: "day",
    fromDateTime: startOfDay(day),
    toDateTime: endOfDay(day),
});

const PowerPUForInvertersChart: FunctionComponent<{ powerPlant: any; }> = ({powerPlant}) => {

    const openSubMenu = useAppSelector((state: any) => state.layout.openSubMenu);
    const {entities, isLoading} = useAppSelector((state: RootState) => state.dashboardNavigationMenu);
    const userConfig = useAppSelector((state: RootState) => state.userConfigurarion);

    const queryClient = useMemo(() => new QueryClient({
        defaultOptions: {
            queries: {
                refetchOnWindowFocus: false,
            },
        },
    }), []);

    const initListSkid = () => {
        const skidList = new Array<any>();
        if(entities){
            entities.filter((element) => element.id === powerPlant.id).map((item) =>
                item.skidList.forEach((skid) => skidList.push(skid))
            );
        }
        return skidList;
    }
    const [entitiesPUList, setEntitiesPUList] = useState<Array<SkidType>>(initListSkid());
    const [date, setDate] = useState<Date | null>(startOfDay(new Date()));
    const [viewMode, setViewMode] = useState<ViewMode>("chart");
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    const period = useMemo(
        () => buildDayPeriod(date ?? startOfDay(new Date())),
        [date]
    );

    useEffect(() => {
        setEntitiesPUList(initListSkid())
    }, [powerPlant, viewMode]);

    useInterval(() => {
        if (viewMode === "chart") {
            setRefreshTrigger((current) => current + 1)
        }
    }, userConfig.mainUpdateInterval, 2000)

    const handleViewModeChange = (_: React.MouseEvent<HTMLElement>, value: string | null) => {
        if (value === "chart" || value === "heatmap") {
            setViewMode(value);
        }
    };

    const handlePeriodChange = (next: Period) => {
        const nextDay = next?.fromDateTime ?? next?.toDateTime;
        if (nextDay) {
            setDate(startOfDay(nextDay));
        }
    };

    const viewModeToggle = (
        <AToggleButtonGroup
            title="Visualização"
            options={[
                { value: "heatmap", label: "Mapa de calor", ariaLabel: "Mapa de calor" },
                { value: "chart", label: "Gráfico", ariaLabel: "Gráfico" },
            ]}
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            styleProps={{
                toggleButtonGroup: {
                    height: "30px",
                    borderRadius: "8px",
                },
                toggleButton: {
                    height: "30px",
                    padding: "8px",
                },
                typography: {
                    fontWeight: 500,
                    fontSize: "13px",
                    lineHeight: "22px",
                    letterSpacing: "0.46px",
                    textTransform: "uppercase",
                },
            }}
        />
    );

    const dayPicker = (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
                label="Dia"
                renderInput={(params) => <TextField
                    sx={{  '& .MuiOutlinedInput-root':
                        { borderRadius: '8px', color: 'gray', height: '30px', width: '155px' } }} {...params} />}
                value={date}
                inputFormat={"DD/MM/YYYY"}
                onChange={(newValue) => {
                    if (newValue && isWithinInterval(new Date(newValue), { start: new Date('1980-01-01'), end: endOfDay(new Date()) })) {
                        setDate(startOfDay(new Date(LocalDateTimeFormatISO(newValue))));
                    }
                    return;
                }}
            />
        </LocalizationProvider>
    );

    if (viewMode === "chart") {
        return (
            <QueryClientProvider client={queryClient}>
                <ActivePowerInvertersChart
                    embedded
                    title={CARD_TITLE}
                    extraHeaderControls={viewModeToggle}
                    headerDateControl={dayPicker}
                    period={period}
                    onPeriodChange={handlePeriodChange}
                    cardHeight={PLANT_CARD_HEIGHT}
                    ariaLabel={CARD_ARIA_LABEL}
                    powerStationData={powerPlant}
                    onDataFound={() => undefined}
                    refreshTrigger={refreshTrigger}
                />
            </QueryClientProvider>
        );
    }

    let heatmapContent: JSX.Element;
    if(isLoading){
        heatmapContent = <Skeleton animation="wave" height={350} variant="rounded" width={'100%'} />
    }else if(entitiesPUList && entitiesPUList.length > 0){
        heatmapContent = <ChartHeatmap skidList={entitiesPUList} date={date} />
    }else{
        heatmapContent = <DataNotFound boxStyle={{height: 350, width: '100%'}}/>
    }

    return (
        <ACard
            key={`chart-power-pu-${openSubMenu}`}
            styleProps={{
                cardStyle: { height: PLANT_CARD_HEIGHT },
                contentStyle: { padding: '0 16px', height: 408, alignItems: 'center'},
                titleStyle: { alignItems: 'center', display: 'flex' },
                subTitleStyle: { display: 'none' },
                headerStyle: { flexDirection: 'row' },
                footerStyle: {paddingTop: 40}
            }}
            title={CARD_TITLE}
            ariaLabel={CARD_ARIA_LABEL}
            headerControlsPosition={"header"}
            headerControls={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexDirection: 'row' }}>
                    {viewModeToggle}
                    {dayPicker}
                </Box>
            }
        >
            {heatmapContent}
        </ACard>
    )
}

export default PowerPUForInvertersChart;
